// ── ShareSemfit — 카카오톡 친구 초대 + 추천 코드 ──
// 사용자가 본인 추천 코드로 셈핏을 친구에게 공유.
// 추천 코드 형식: 사용자 uid의 앞 6자 (대문자).
// 공유 URL에 ?ref=<code> 붙여서 추후 가입 시 트래킹 가능.

import React, { useState, useEffect } from 'react';

const SEMFIT_URL = 'https://semfit.vercel.app';
const KAKAO_KEY = process.env.REACT_APP_KAKAO_KEY;

// 사용자 uid → 추천 코드 (6자 대문자)
function generateReferralCode(uid) {
  if (!uid) return '';
  return uid.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase();
}

export default function ShareSemfit({ user, compact = false }) {
  const [copied, setCopied] = useState(false);
  const [kakaoReady, setKakaoReady] = useState(false);

  const refCode = generateReferralCode(user?.uid);
  const shareUrl = refCode
    ? `${SEMFIT_URL}/?ref=${refCode}&utm_source=referral&utm_medium=kakao`
    : SEMFIT_URL;

  // SDK 스크립트가 비동기 로드되므로 모바일/느린 환경에선 mount 시점에 window.Kakao가 없을 수 있음.
  // 최대 ~3초 동안 100ms 간격으로 폴링하면서 init 시도.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;
    let attempts = 0;
    const tryInit = () => {
      if (cancelled) return;
      if (window.Kakao) {
        if (!window.Kakao.isInitialized() && KAKAO_KEY) {
          try {
            window.Kakao.init(KAKAO_KEY);
          } catch (e) {
            console.warn('Kakao init 실패:', e.message);
          }
        }
        const ready = !!(window.Kakao && window.Kakao.isInitialized());
        setKakaoReady(ready);
        if (ready) return;
      }
      attempts += 1;
      if (attempts < 30) setTimeout(tryInit, 100);
    };
    tryInit();
    return () => { cancelled = true; };
  }, []);

  const handleKakaoShare = () => {
    if (!window.Kakao || !window.Kakao.isInitialized()) {
      alert('카카오톡 공유 기능을 불러올 수 없습니다. 잠시 후 다시 시도해주세요.');
      return;
    }
    try {
      window.Kakao.Share.sendDefault({
        objectType: 'feed',
        content: {
          title: '셈핏 — 외식업 사장님의 운영 OS',
          description:
            '원가·세무·인건비·재고·공동구매까지 한 곳에서. 한정 100매장 무료 베타 모집 중!',
          imageUrl: `${SEMFIT_URL}/og-image.png?v=2`,
          link: {
            mobileWebUrl: shareUrl,
            webUrl: shareUrl,
          },
        },
        buttons: [
          {
            title: '셈핏 무료 베타 신청',
            link: {
              mobileWebUrl: shareUrl,
              webUrl: shareUrl,
            },
          },
        ],
      });
    } catch (e) {
      console.error('카카오 공유 실패:', e);
      // fallback to copy
      handleCopy();
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (compact) {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          onClick={handleKakaoShare}
          disabled={!kakaoReady}
          style={{
            padding: '8px 14px', background: '#FEE500', color: '#3C1E1E',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: kakaoReady ? 'pointer' : 'not-allowed', opacity: kakaoReady ? 1 : 0.5,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          💬 카카오톡 공유
        </button>
        <button
          onClick={handleCopy}
          style={{
            padding: '8px 14px', background: '#fff', color: '#EA580C',
            border: '1px solid #EA580C', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {copied ? '✓ 복사됨' : '🔗 링크 복사'}
        </button>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff', borderRadius: 16,
      padding: 20, border: '0.5px solid rgba(44,58,48,0.1)',
    }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600, color: '#2C3A30' }}>
        🎁 친구 사장님 초대하기
      </h3>
      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6B7A71', lineHeight: 1.6 }}>
        내 추천 링크로 친구가 가입하면 두 분 모두 <strong style={{ color: '#B55041' }}>Pro 1개월 무료</strong>!
        한정 100매장 베타이니 지금이 기회예요.
      </p>

      {refCode && (
        <div style={{
          padding: '10px 14px', background: '#F7F4ED', borderRadius: 8,
          marginBottom: 12, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 8,
        }}>
          <div>
            <div style={{ fontSize: 11, color: '#6B7A71' }}>내 추천 코드</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#EA580C', letterSpacing: 2 }}>
              {refCode}
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#6B7A71', textAlign: 'right' }}>
            가입 시 사용
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={handleKakaoShare}
          disabled={!kakaoReady}
          style={{
            flex: 1, minWidth: 140,
            padding: '12px 16px', background: '#FEE500', color: '#3C1E1E',
            border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
            cursor: kakaoReady ? 'pointer' : 'not-allowed', opacity: kakaoReady ? 1 : 0.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          💬 카카오톡으로 공유
        </button>
        <button
          onClick={handleCopy}
          style={{
            flex: 1, minWidth: 140,
            padding: '12px 16px', background: '#fff', color: '#EA580C',
            border: '1.5px solid #EA580C', borderRadius: 10, fontSize: 14, fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {copied ? '✓ 링크 복사됨' : '🔗 링크 복사'}
        </button>
      </div>

      <div style={{
        marginTop: 10, fontSize: 11, color: '#6B7A71',
        wordBreak: 'break-all', background: '#FAFAF7',
        padding: '8px 10px', borderRadius: 6,
      }}>
        {shareUrl}
      </div>

      {!kakaoReady && (
        <div style={{
          marginTop: 10, padding: '8px 12px', background: '#FFFBEB',
          borderRadius: 6, fontSize: 11, color: '#92400E',
        }}>
          ⚠️ 카카오톡 공유가 비활성 상태예요. REACT_APP_KAKAO_KEY 환경변수가 설정돼야 합니다.
        </div>
      )}
    </div>
  );
}
